import { useCallback, useMemo } from "react";
import { ArrowUpRight, MapPin, Video } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { FavoriteButton } from "@/components/FavoriteButton";
import { EventImage } from "@/components/EventImage";
import { cn, formatBRLOrFree } from "@/lib/utils";
import { getCategoryLabel } from "@/lib/categories";
import { getEventBySlug } from "@/lib/api";
import { getEventPublicPath } from "@/lib/urls";

interface EventCardProps {
  title: string;
  date: string;
  city: string;
  imageUrl: string | null | undefined;
  priceFrom: number;
  category?: string;
  slug?: string;
  producerSlug?: string;
  isOrg?: boolean;
  eventId?: string;
  className?: string;
  /** Prioridade de carregamento da imagem — use true APENAS no primeiro card visível */
  priority?: boolean;
  variant?: "grid" | "list";
  startDate?: string | null;
  endDate?: string | null;
  venueName?: string | null;
  isOnline?: boolean;
}

function parseEventDateInfo(
  startDate?: string | null,
  endDate?: string | null,
  fallbackDate?: string,
  locale: string = "pt"
) {
  let parsedStart: Date | null = null;
  if (startDate) {
    const d = new Date(startDate);
    if (!isNaN(d.getTime())) parsedStart = d;
  }

  let parsedEnd: Date | null = null;
  if (endDate) {
    const d = new Date(endDate);
    if (!isNaN(d.getTime())) parsedEnd = d;
  }

  const intlLocale = locale.startsWith("en") ? "en-US" : "pt-BR";

  if (parsedStart) {
    // Mês curto em 3 letras maiúsculas (ex: "SET", "OUT", "SEP")
    const monthRaw = new Intl.DateTimeFormat(intlLocale, { month: "short" }).format(parsedStart);
    const month = monthRaw.replace(".", "").toUpperCase().slice(0, 3);

    // Dia do mês
    const day = new Intl.DateTimeFormat(intlLocale, { day: "numeric" }).format(parsedStart);

    // Dia da semana curto (ex: "Qua", "Qui", "Thu")
    const weekdayShort = new Intl.DateTimeFormat(intlLocale, { weekday: "short" }).format(parsedStart);
    const cleanWeekday = weekdayShort.replace(".", "");
    const capitalizedWeekday = cleanWeekday.charAt(0).toUpperCase() + cleanWeekday.slice(1);

    const dayMonth = new Intl.DateTimeFormat(intlLocale, { day: "numeric", month: "long" }).format(parsedStart);
    const dateTitle = `${capitalizedWeekday}, ${dayMonth}`;

    // Horário formatado
    const startTime = new Intl.DateTimeFormat(intlLocale, { hour: "2-digit", minute: "2-digit" }).format(parsedStart);
    let timeDesc = startTime;
    if (parsedEnd) {
      const endTime = new Intl.DateTimeFormat(intlLocale, { hour: "2-digit", minute: "2-digit" }).format(parsedEnd);
      timeDesc = `${startTime} – ${endTime}`;
    }

    return { month, day, dateTitle, timeDesc };
  }

  // Fallback se apenas a string formatada foi passada
  if (fallbackDate) {
    const match = fallbackDate.match(/(\d{1,2})\s*(?:de\s*)?([a-zA-ZçÇ]{3,})/i);
    if (match) {
      return {
        month: match[2].slice(0, 3).toUpperCase(),
        day: match[1],
        dateTitle: fallbackDate,
        timeDesc: "Horário no local",
      };
    }
  }

  return {
    month: "EVT",
    day: "—",
    dateTitle: fallbackDate || "Data a confirmar",
    timeDesc: "Horário a definir",
  };
}

export function EventCard({
  title,
  date,
  city,
  imageUrl,
  priceFrom,
  category,
  slug,
  producerSlug,
  isOrg,
  eventId,
  className,
  priority,
  variant = "grid",
  startDate,
  endDate,
  venueName,
  isOnline,
}: EventCardProps) {
  const { t, i18n } = useTranslation("landingHome");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const Wrapper = slug ? Link : "div";
  const eventPath = slug ? getEventPublicPath(slug, producerSlug, isOrg) : undefined;
  const wrapperProps = slug ? { to: eventPath } : {};

  const { month, day, dateTitle, timeDesc } = useMemo(
    () => parseEventDateInfo(startDate, endDate, date, i18n.language),
    [startDate, endDate, date, i18n.language]
  );

  const isOnlineEvent = Boolean(
    isOnline ||
      city?.toLowerCase().includes("online") ||
      venueName?.toLowerCase().includes("online")
  );

  // Normalização refinada de local para evitar redundância (ex: Toledo / Toledo)
  const { locationTitle, locationDesc, hasMapArrow } = useMemo(() => {
    if (isOnlineEvent) {
      return {
        locationTitle: t("eventCard.onlineEvent", "Evento Online"),
        locationDesc: t("eventCard.liveStream", "Transmissão ao vivo"),
        hasMapArrow: false,
      };
    }

    const cleanVenue = venueName?.trim();
    const cleanCity = city?.trim();

    if (cleanVenue && cleanVenue.toLowerCase() !== cleanCity?.toLowerCase()) {
      return {
        locationTitle: cleanVenue,
        locationDesc: cleanCity || "Presencial",
        hasMapArrow: true,
      };
    }

    return {
      locationTitle: cleanCity || "Presencial",
      locationDesc: "Local a confirmar",
      hasMapArrow: false,
    };
  }, [isOnlineEvent, venueName, city, t]);

  // Ao hover: pré-carrega o chunk JS do EventDetail + dados do evento
  const handlePrefetch = useCallback(() => {
    if (!slug) return;
    import("@/pages/EventDetail");
    queryClient.prefetchQuery({
      queryKey: ["event", slug],
      queryFn: () => getEventBySlug(slug),
      staleTime: 5 * 60 * 1000,
    });
  }, [slug, queryClient]);

  const handleBuyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (slug) {
      navigate(getEventPublicPath(slug, producerSlug, isOrg));
    }
  };

  if (variant === "list") {
    return (
      <Wrapper
        {...(wrapperProps as any)}
        onMouseEnter={handlePrefetch}
        className={cn(
          "group relative flex flex-col sm:flex-row rounded-[24px] overflow-hidden border border-border/70 bg-card shadow-[0_2px_8px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.07)] hover:border-border/90 hover:-translate-y-0.5 transition-all duration-200 ease-out cursor-pointer w-full gap-4 sm:gap-5 p-4 sm:p-5",
          className
        )}
      >
        <div className="relative aspect-[16/10] w-full sm:w-60 md:w-64 shrink-0 overflow-hidden rounded-[18px] bg-muted">
          <EventImage
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
            priority={priority}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Categoria em badge de vidro fosco */}
          {category && (
            <div className="absolute bottom-2.5 left-2.5 z-10">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-medium tracking-wide bg-black/45 backdrop-blur-md text-white border border-white/15 shadow-xs">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {getCategoryLabel(category)}
              </span>
            </div>
          )}

          {eventId && (
            <FavoriteButton
              eventId={eventId}
              className="absolute top-2.5 right-2.5 z-10 backdrop-blur-md bg-black/30 hover:bg-black/50 text-white border border-white/20 transition-all shadow-xs"
            />
          )}
        </div>

        <div className="flex-1 flex flex-col justify-between py-0.5 min-w-0">
          <div className="space-y-3">
            <h3 className="font-display font-semibold text-lg md:text-xl text-foreground group-hover:text-primary transition-colors leading-snug">
              {title}
            </h3>

            {/* Squircles Luma/Apple (Data e Local) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
              {/* Data */}
              <div className="flex items-center gap-3">
                <div className="w-9 sm:w-10 h-10 sm:h-11 shrink-0 rounded-xl overflow-hidden border border-border/80 bg-card flex flex-col shadow-[0_1px_2px_rgba(0,0,0,0.04)] group-hover:border-border transition-colors">
                  <div className="bg-muted/80 dark:bg-muted/40 text-[8.5px] uppercase font-bold tracking-[0.1em] text-muted-foreground text-center py-0.5 border-b border-border/60 leading-none select-none">
                    {month}
                  </div>
                  <div className="flex-1 flex items-center justify-center font-display font-bold text-xs sm:text-sm text-foreground leading-none tabular-nums select-none">
                    {day}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs sm:text-[13px] font-semibold text-foreground truncate">
                    {dateTitle}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {timeDesc}
                  </div>
                </div>
              </div>

              {/* Local */}
              <div className="flex items-center gap-3">
                <div className="size-9 sm:size-10 shrink-0 rounded-xl border border-border/80 bg-secondary/50 flex items-center justify-center text-foreground/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)] group-hover:border-border transition-colors">
                  {isOnlineEvent ? (
                    <Video className="h-4 w-4 text-primary" />
                  ) : (
                    <MapPin className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs sm:text-[13px] font-semibold text-foreground flex items-center gap-1 truncate">
                    <span className="truncate">{locationTitle}</span>
                    {hasMapArrow && (
                      <ArrowUpRight className="h-3 w-3 opacity-40 shrink-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-muted-foreground" />
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {locationDesc}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
            <div className="flex flex-col">
              <span className="text-[9px] uppercase tracking-wider text-muted-foreground/75 font-medium">
                {t("eventCard.priceFrom")}
              </span>
              <span className="font-display text-lg sm:text-xl font-bold text-foreground tabular-nums tracking-tight">
                {formatBRLOrFree(priceFrom)}
              </span>
            </div>
            <Button
              size="default"
              variant="default"
              className="h-9 sm:h-10 px-6 rounded-full font-semibold shadow-none transition-transform active:scale-[0.97]"
              onClick={handleBuyClick}
            >
              {priceFrom === 0 ? t("eventCard.buyFree") : t("eventCard.buyPaidTickets")}
            </Button>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      {...(wrapperProps as any)}
      onMouseEnter={handlePrefetch}
      className={cn(
        "group relative rounded-[24px] overflow-hidden border border-border/70 bg-card shadow-[0_2px_8px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.07)] hover:border-border/90 hover:-translate-y-0.5 transition-all duration-200 ease-out cursor-pointer flex flex-col justify-between",
        className
      )}
    >
      {/* Imagem com Categoria e Favoritos em vidro fosco */}
      <div className="aspect-[16/10] relative overflow-hidden bg-muted">
        <EventImage
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
          priority={priority}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {category && (
          <div className="absolute bottom-2.5 left-2.5 z-10">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium tracking-wide bg-black/45 backdrop-blur-md text-white border border-white/15 shadow-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {getCategoryLabel(category)}
            </span>
          </div>
        )}

        {eventId && (
          <FavoriteButton
            eventId={eventId}
            className="absolute top-2.5 right-2.5 z-10 backdrop-blur-md bg-black/30 hover:bg-black/50 text-white border border-white/20 transition-all shadow-xs"
          />
        )}
      </div>

      <div className="p-3.5 sm:p-4 flex-1 flex flex-col justify-between space-y-3">
        <div className="space-y-2.5">
          <h3 className="font-display font-semibold text-base sm:text-[15px] text-foreground line-clamp-2 leading-snug group-hover:text-primary transition-colors tracking-tight">
            {title}
          </h3>

          {/* Squircles Luma/Apple (Data e Local) */}
          <div className="space-y-2 pt-0.5">
            {/* Data */}
            <div className="flex items-center gap-2.5">
              <div className="w-8 sm:w-9 h-9 sm:h-10 shrink-0 rounded-xl overflow-hidden border border-border/80 bg-card flex flex-col shadow-[0_1px_2px_rgba(0,0,0,0.04)] group-hover:border-border transition-colors">
                <div className="bg-muted/80 dark:bg-muted/40 text-[10px] uppercase font-bold tracking-[0.1em] text-muted-foreground text-center py-0.5 border-b border-border/60 leading-none select-none">
                  {month}
                </div>
                <div className="flex-1 flex items-center justify-center font-display font-bold text-[11px] sm:text-xs text-foreground leading-none tabular-nums select-none">
                  {day}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm sm:text-xs font-semibold text-foreground truncate">
                  {dateTitle}
                </div>
                <div className="text-xs sm:text-[11px] text-muted-foreground truncate mt-0.5">
                  {timeDesc}
                </div>
              </div>
            </div>

            {/* Local */}
            <div className="flex items-center gap-2.5">
              <div className="size-8 sm:size-9 shrink-0 rounded-xl border border-border/80 bg-secondary/50 flex items-center justify-center text-foreground/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)] group-hover:border-border transition-colors">
                {isOnlineEvent ? (
                  <Video className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm sm:text-xs font-semibold text-foreground flex items-center gap-1 truncate">
                  <span className="truncate">{locationTitle}</span>
                  {hasMapArrow && (
                    <ArrowUpRight className="h-2.5 sm:h-3 w-2.5 sm:w-3 opacity-40 shrink-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-muted-foreground" />
                  )}
                </div>
                <div className="text-xs sm:text-[11px] text-muted-foreground truncate mt-0.5">
                  {locationDesc}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-stretch justify-between gap-3 pt-3 border-t border-border/50 mt-1 sm:flex-row sm:items-center">
          <div className="flex flex-col">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              {t("eventCard.priceFrom")}
            </span>
            <span className="font-display text-base sm:text-sm font-bold text-foreground tabular-nums tracking-tight">
              {formatBRLOrFree(priceFrom)}
            </span>
          </div>
          <Button
            size="sm"
            variant="default"
            className="h-11 w-full sm:h-8 sm:w-auto px-3.5 sm:px-4 rounded-full text-sm sm:text-xs font-semibold shadow-none transition-transform active:scale-[0.96] bg-primary hover:bg-primary/90 text-primary-foreground"
            onClick={handleBuyClick}
          >
            {priceFrom === 0 ? t("eventCard.buyFree") : t("eventCard.buyPaid")}
          </Button>
        </div>
      </div>
    </Wrapper>
  );
}
